import { MessageFilled, MessageOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { FC, useCallback, useState } from "react";
import { TaskAssignmentPayload, TaskStatus } from "tm-shared/types";

import { BottomSheet } from "@/components/BottomSheet";
import { StatusCycleButton } from "@/components/CycleButton";
import FlexContainer from "@/components/FlexContainer";
import { CommentSelectModal } from "@/components/Modals/CommentSelectModal";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { StatusIndicator } from "@/components/Status/StatusIndicator";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch } from "@/hooks/redux";
import { patchAssignmentsEntity } from "@/store/slices/progress";

import { StyledBottomSheetTop } from "../../Styled";
import { StyledBooleanAssignment, StyledCommentIndicator } from "./Styled";

interface BoolProps {
    marketId: string;
    taskId: string;
    payload: TaskAssignmentPayload;
    onChangeStatus: (status: TaskStatus) => void;
}

export const Bool: FC<BoolProps> = ({
    payload,
    marketId,
    taskId,
    onChangeStatus,
}) => {
    const [modal, setModal] = useState<"comment" | null>(null);
    const [open, setOpen] = useState(false);
    const hasComment = !!payload?.comment;
    const status = payload?.status ?? TaskStatus.Incomplete;
    const dispatch = useAppDispatch();

    const handleChangeStatus = useCallback((status: TaskStatus) => {
        onChangeStatus(status);
    }, []);

    const onChangeComment = useCallback(
        (comment: string) => {
            dispatch(
                patchAssignmentsEntity({
                    taskId,
                    patch: { comment },
                }),
            );
        },
        [dispatch, marketId, taskId],
    );

    const handleOpenBottomSheet = useCallback(() => {
        setOpen(true);
    }, []);

    const handleCloseBottomSheet = useCallback(() => {
        setOpen(false);
    }, []);

    return (
        <>
            <StyledBooleanAssignment onClick={handleOpenBottomSheet}>
                <PrimaryText>Підтвердження</PrimaryText>

                <FlexContainer>
                    <StyledCommentIndicator>
                        {hasComment && (
                            <StatusIndicator type="warning">
                                <MessageFilled />
                            </StatusIndicator>
                        )}
                    </StyledCommentIndicator>

                    <StatusCycleButton
                        value={status}
                        onChange={handleChangeStatus}
                        dimension={48}
                        border={false}
                    />
                </FlexContainer>
            </StyledBooleanAssignment>

            <Flex
                style={{
                    width: "100%",
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                }}
            >
                <BottomSheet open={open} onClose={handleCloseBottomSheet}>
                    <StyledBottomSheetTop>
                        <PrimaryText>Підтвердження</PrimaryText>
                        <FlexContainer gap="20px">
                            <OutlineIconButton
                                icon={<MessageOutlined />}
                                onClick={() => setModal("comment")}
                                disabled={
                                    !payload?.status ||
                                    payload?.status === TaskStatus.Incomplete
                                }
                            />
                            <StatusCycleButton
                                value={payload?.status ?? TaskStatus.Incomplete}
                                onChange={onChangeStatus}
                            />
                        </FlexContainer>
                    </StyledBottomSheetTop>
                </BottomSheet>
            </Flex>

            {modal === "comment" && (
                <CommentSelectModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={onChangeComment}
                    initialComment={payload?.comment ?? ""}
                />
            )}
        </>
    );
};
